// [COMMANDES-STATUTS] Affichage commandes espace cliente
import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { useCustomerAuth } from "../components/CustomerLayout";
import {
  ShoppingBag, Loader2, Check, CheckCheck,
  Truck, XCircle, MessageCircle, ArrowRight
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// [COMMANDES-STATUTS] Status badge config
const STATUS_CONFIG = {
  confirmee: { label: "Confirmée", bg: "rgba(34,197,94,0.12)", text: "#34D399", icon: Check },
  confirmed: { label: "Confirmée", bg: "rgba(34,197,94,0.12)", text: "#34D399", icon: Check },
  en_cours: { label: "En préparation", bg: "rgba(249,115,22,0.12)", text: "#FB923C", icon: Loader2 },
  expediee: { label: "En cours de livraison", bg: "rgba(99,102,241,0.12)", text: "#818CF8", icon: Truck },
  shipped: { label: "En cours de livraison", bg: "rgba(99,102,241,0.12)", text: "#818CF8", icon: Truck },
  terminee: { label: "Livrée - Terminée", bg: "rgba(34,197,94,0.15)", text: "#22C55E", icon: CheckCheck },
  completed: { label: "Livrée - Terminée", bg: "rgba(34,197,94,0.15)", text: "#22C55E", icon: CheckCheck },
  annulee: { label: "Annulée", bg: "rgba(239,68,68,0.1)", text: "#FCA5A5", icon: XCircle },
  cancelled: { label: "Annulée", bg: "rgba(239,68,68,0.1)", text: "#FCA5A5", icon: XCircle },
};

const formatShortDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000 && d.getDate() === now.getDate()) return "Aujourd'hui";
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.getDate() === yesterday.getDate() && d.getMonth() === yesterday.getMonth()) return "Hier";
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long" });
};

const getOrderRef = (order) => {
  if (order.order_number) return order.order_number;
  const id = order.id || "";
  return "CMD-" + id.slice(-4).toUpperCase();
};

export default function CustomerOrders() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, devise } = useCustomerAuth();
  const accent = "#D4A853"; // boutique accent fallback

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const prevOrdersRef = useRef([]);
  const pollRef = useRef(null);

  // [COMMANDES-STATUTS] Fetch all orders sorted by createdAt desc
  const fetchOrders = useCallback(async (silent = false) => {
    try {
      const res = await axios.get(`${API}/api/customer/orders`, getAuthHeaders());
      // [COMMANDES-STATUTS] Filter only validated orders
      const validStatuses = ["confirmed", "confirmee", "en_cours", "shipped", "expediee", "completed", "terminee", "cancelled", "annulee"];
      const sorted = (res.data || [])
        .filter(o => validStatuses.includes(o.status))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      // [COMMANDES-STATUTS] Detect status changes for toast
      if (prevOrdersRef.current.length > 0) {
        sorted.forEach(o => {
          const prev = prevOrdersRef.current.find(p => p.id === o.id);
          if (prev && prev.status !== o.status) {
            const item = o.items?.[0]?.product_name || "votre commande";
            const sc = STATUS_CONFIG[o.status];
            toast.success(`Votre commande ${item} a été mise à jour : ${sc?.label || o.status}`, {
              duration: 3000,
              style: { background: accent, color: "#fff", border: "none" },
            });
          }
        });
      }
      prevOrdersRef.current = sorted;
      setOrders(sorted);
    } catch {
      if (!silent) toast.error("Erreur lors du chargement des commandes");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, accent]);

  // [COMMANDES-STATUTS] Polling every 30s
  useEffect(() => {
    fetchOrders();
    pollRef.current = setInterval(() => fetchOrders(true), 30000);
    return () => clearInterval(pollRef.current);
  }, [fetchOrders]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
        <Loader2 size={28} className="animate-spin" style={{ color: accent }} />
      </div>
    );
  }

  // [COMMANDES-STATUTS] Empty state
  if (orders.length === 0) {
    return (
      <div style={{ padding: "48px 24px", textAlign: "center" }}>
        <div style={{
          width: 72, height: 72, borderRadius: "50%", margin: "0 auto 20px",
          background: "#1A1A1A", display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ShoppingBag size={32} style={{ color: "#333" }} />
        </div>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
          Aucune commande pour l'instant
        </div>
        <div style={{ color: "#9CA3AF", fontSize: 13, lineHeight: 1.5, marginBottom: 24 }}>
          Exprimez votre intérêt sur un article pour commencer !
        </div>
        <Link to={`/vitrine/${userId}`} style={{ textDecoration: "none" }}>
          <button data-testid="empty-orders-cta" style={{
            padding: "12px 24px", borderRadius: 12, border: "none", cursor: "pointer",
            background: accent, color: "#fff", fontWeight: 600, fontSize: 14,
            fontFamily: "inherit",
          }}>
            Voir la boutique <ArrowRight size={14} style={{ marginLeft: 4, verticalAlign: "middle" }} />
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 16px" }}>
      {/* Header */}
      <div style={{ marginBottom: 16, display: "flex", alignItems: "baseline", gap: 8 }}>
        <h2 data-testid="orders-header" style={{ color: "#fff", fontWeight: 700, fontSize: 16, margin: 0 }}>
          Mes commandes
        </h2>
        <span style={{ color: "#9CA3AF", fontSize: 14 }}>({orders.length})</span>
      </div>

      {/* Orders list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <AnimatePresence>
          {orders.map((order) => {
            const sc = STATUS_CONFIG[order.status] || STATUS_CONFIG.confirmee;
            const Icon = sc.icon;
            const firstItem = order.items?.[0];
            const imgSrc = firstItem?.image_url || firstItem?.image;
            const price = order.final_price || order.proposed_price || order.total || order.subtotal || 0;
            const isViaChat = order.source === "proposition" || order.conversation_id;

            return (
              <motion.div
                key={order.id}
                data-testid={`customer-order-${order.id}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                style={{
                  background: "#1A1A1A", borderRadius: 14, padding: 16,
                  border: "1px solid #2A2A2A",
                }}
              >
                {/* Card header: ref + date */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <span style={{ color: "#9CA3AF", fontSize: 12, fontFamily: "monospace" }}>
                    {getOrderRef(order)}
                  </span>
                  <span style={{ color: "#6B7280", fontSize: 12 }}>
                    {formatShortDate(order.created_at)}
                  </span>
                </div>

                {/* Card body: image + info */}
                <div style={{ display: "flex", gap: 12 }}>
                  {imgSrc ? (
                    <img src={imgSrc.startsWith("data:") ? imgSrc : imgSrc.startsWith("http") ? imgSrc : `data:image/jpeg;base64,${imgSrc}`}
                      alt="" style={{ width: 60, height: 60, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
                  ) : (
                    <div style={{
                      width: 60, height: 60, borderRadius: 10, flexShrink: 0,
                      background: "#0F0F0F", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <span style={{ color: "#444", fontWeight: 700, fontSize: 16 }}>
                        {(firstItem?.product_name || "?").substring(0, 2).toUpperCase()}
                      </span>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {firstItem?.product_name || "Commande"}
                      {order.items?.length > 1 && <span style={{ color: "#9CA3AF", fontWeight: 400, fontSize: 12 }}> +{order.items.length - 1}</span>}
                    </div>
                    <div style={{ color: "#9CA3AF", fontSize: 12, marginTop: 2 }}>
                      {[firstItem?.size, firstItem?.color].filter(Boolean).join(" · ")}
                    </div>
                    <div style={{ color: accent, fontWeight: 700, fontSize: 15, marginTop: 4 }}>
                      {price.toLocaleString("fr-FR")} {devise}
                    </div>

                    {/* Status badge */}
                    <div style={{
                      display: "inline-flex", alignItems: "center", gap: 5, marginTop: 8,
                      padding: "4px 12px", borderRadius: 20,
                      background: sc.bg, fontSize: 11, fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: 0.4, color: sc.text,
                    }} data-testid={`badge-${order.status}`}>
                      <Icon size={12} /> {sc.label}
                    </div>
                  </div>
                </div>

                {/* Card footer */}
                <div style={{
                  borderTop: "1px solid #2A2A2A", paddingTop: 10, marginTop: 12,
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                }}>
                  <div>
                    {isViaChat && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        padding: "3px 10px", borderRadius: 20,
                        background: "rgba(99,102,241,0.1)", color: "#818CF8",
                        fontSize: 11, fontWeight: 500,
                      }}>
                        <MessageCircle size={11} /> Via conversation
                      </span>
                    )}
                  </div>
                  {isViaChat && (
                    <button
                      data-testid={`chat-link-${order.id}`}
                      onClick={() => navigate(`/vitrine/${userId}/espace/messages`)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: accent, fontSize: 13, fontWeight: 600,
                        display: "flex", alignItems: "center", gap: 4,
                        fontFamily: "inherit", padding: 0,
                      }}
                    >
                      Voir le chat <ArrowRight size={13} />
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
